import React, {useEffect, useState, useRef} from "react";
import GenericLayout from "layouts/generic";
import css from "./index.module.scss";
import Card from "@/components/card";
import Button from "@/components/button";
import ContactSection from "sections/contact";
import ArrowDownLongSvg from "/public/images/icons/arrowDownLong.svg";
import Picture from "@/components/picture";
import { useInView } from "react-intersection-observer";
import SplitText from "@/components/splitText";
import slideFadeIn from "scripts/animations/slideFadeIn";
import slideFadeInSlow from "scripts/animations/slideFadeInSlow.js";
import popFadeIn from "scripts/animations/popFadeIn";
import inViewSettings from "scripts/animations/inViewSettings";
import imageSlideFadeIn from "scripts/animations/imageSlideFadeIn";
import TacticsSmall, {TACTICS} from "@/sections/tacticsSmall";
import Articles from "@/sections/articles";
import strapi from "scripts/strapi";
import TestimonialsSpotlight, {TESTIMONIALS} from "@/sections/testimonialsSpotlight";
import BooksSvg from "/public/images/icons/books.svg";
import GearComplexCodeSvg from "/public/images/icons/gearComplexCode.svg";
import PenNibSvg from "/public/images/icons/penNib.svg";
import TextSizeSvg from "/public/images/icons/textSize.svg";
import FlowChartSection from "@/sections/flowChart";

export default function LandingPageDevelopment({articles}) {
  const [elements, setElements] = useState({
    intro: {
      image: useInView(inViewSettings(imageSlideFadeIn)),
      text: useInView(inViewSettings(slideFadeIn)),
    },
    benefits: {
      text: useInView(inViewSettings(slideFadeIn)),
      cards: useInView(inViewSettings(popFadeIn)),
    },
    development: {
      text: useInView(inViewSettings(slideFadeIn)),
      cards: useInView(inViewSettings(slideFadeInSlow)),
    },
    tactics: {
      text: useInView(inViewSettings(slideFadeIn)),
      cards: useInView(inViewSettings(popFadeIn)),
    },
    articles: {
      text: useInView(inViewSettings(slideFadeIn)),
      cards: useInView(inViewSettings(popFadeIn)),
    },
  });

  return <GenericLayout
    title="Landing Page Development | Deviate Labs"
    description="We craft high-converting landing pages that captivate visitors and drive leads with compelling copy, intuitive design, and technical optimization."
    color="purple"
  >
    <section className={`section ${css.fold}`}>
      <div className="sectionInner">
        <div className={css.left}>
          <h2>
            <div>Landing Pages That Convert</div>
            <div>Visitors into Customers</div>
          </h2>
          <div className={css.subheadline}>Our front-end development experts build high-converting pages to capture leads and drive sales for your business.</div>
          <Button label="Let's Talk" color="purple" className={css.cta} href="#contact"/>
        </div>
        <div className={css.right}>
          <Picture
            base={"/images/misc/dtPhone/dtPhone"}
            exts={["webp", "png"]}
            widths={[500, 1000]}
            loading="eager"
            sizes="500px"
            alt="Phone mockup"
          />
        </div>
      </div>
    </section>
    <section className={`section ${css.intro}`}>
      <div className="sectionInner">
        <div className={`${css.image} target`} ref={elements.intro.image.ref}>
          <Picture
            base={"/images/misc/bullseye/bullseye"}
            exts={["webp", "png"]}
            widths={[100, 200, 300, 400, 500, 600, 700]}
            loading="eager"
            sizes="max(900px) 100vw, 500px"
            alt="Laptop mockup"
          />
        </div>
        <div className={css.text} ref={elements.intro.text.ref}>
          <h2>
            <SplitText className="target" text="An Essential Marketing Tactic"/>
          </h2>
          <p>
            <SplitText className="target" text="Landing pages are the first interaction customers have with your brand online. That&apos;s why an effective, high-converting landing page is essential for capturing contacts and driving conversions."/>
          </p>
          <p>
            <SplitText className="target" text="Our design experts know how to optimize every element on your page, from the layout to the copy to the call-to-action. We research your audience, design an intuitive layout, and write compelling copy that speaks directly to your customers. With our proven landing page development process, we can help you connect with more qualified prospects and grow your business."/>
          </p>
        </div>
      </div>
    </section>

    <section className={`section ${css.benefits}`}>
      <div className="sectionInner" >
        <div ref={elements.benefits.text.ref}>
          <h2>
            <SplitText className="target" text="Custom Landing Page Benefits"/>
          </h2>
          <div className={css.subheadline}>
            <SplitText className="target" text="Our landing pages deliver results through increased conversions, captured leads, sales opportunities, and brand alignment."/>
          </div>
        </div>

        <div className={css.cards} ref={elements.benefits.cards.ref}>
          <Card light className={`target ${css.card}`} headline="Increased Conversions">
            <div className={css.cardInner}>We continuously analyze your analytics to identify opportunities and A/B test elements like headlines and calls-to-action. Our clients typically see conversion rates double or even triple (compared to driving traffic to the homepage).</div>
          </Card>
          <Card light className={`target ${css.card}`} headline="Higher Sales">
            <div className={css.cardInner}>With a high-converting landing page, you&apos;ll turn more of your website traffic into leads. We&apos;ll ensure your page speaks directly to your target customer and compels them to take action through clear, benefit-driven copy and seamless lead capture forms.</div>
          </Card>
          <Card light className={`target ${css.card}`} headline="Brand Consistency">
            <div className={css.cardInner}>Our designers ensure your landing pages align with your brand styleguide. This creates a consistent experience across touchpoints and strengthens brand recognition and trust with visitors.</div>
          </Card>
        </div>
      </div>
    </section>

    <FlowChartSection
      headline="Expert Development at Every Stage"
      subheadline="Our systematic approach to landing page creation ensures optimal performance."
      cards={[
        {
          headline: "Step 1: Research",
          body: "We dive into your business goals, target audience, and competitors to inform strategic decisions.",
          icon: <BooksSvg/>,
        },
        {
          headline: "Step 2: Design",
          body: "Our designers craft intuitive, branded layouts that engage your audience.",
          icon: <PenNibSvg/>,
        },
        {
          headline: "Step 3: Copywriting",
          body: "Compelling copy keeps visitors engaged and persuades them to convert.",
          icon: <TextSizeSvg/>,
        },
        {
          headline: "Step 4: Optimization",
          body: "We continuously test and optimize elements like headlines, images, and calls-to-action.",
          icon: <GearComplexCodeSvg/>,
        },
      ]}
    />

    <TestimonialsSpotlight testimonial={TESTIMONIALS.RADIX}/>


    <ContactSection compact/>

    <TacticsSmall enabled={[
      TACTICS.CONTENT_MARKETING,
      TACTICS.B2B_SAAS_GROWTH_MARKETING,
      TACTICS.SONIC_BRANDING,
      TACTICS.GROWTH_MARKETING,
      TACTICS.STUNT_MARKETING,
    ]}/>
    <Articles
      articles={articles}
      headline="Learn More About Landing Pages"
      subheadline="We've helped hundreds of clients boost conversions with optimized landing pages."
    />

  </GenericLayout>;
}


export async function getStaticProps() {
  let data = await strapi.article.findMany({
    filters: {
      id: {
        $eq: [33, 32, 27],
      },
    },
    populate: {
      coverArt: "*",
    },
    fields: [ //don't include the body since it's large and unnecessary
      "headline",
      "slug",
      "teaser",
      "priority",
      "category",
      "publishedAt",
    ],
  });

  return {
    props: {
      articles: data,
    },
    revalidate: 60,
  };
}
